/**
 * "Which task?" - the only form in the feature that has to ask.
 *
 * Every other way into a timer already knows the task, because it is drawn on
 * the task: the button in a task's head, the row in a register menu. The top bar
 * is not on any task at all, so the way in from there has to pick one first, and
 * that is the whole reason this file exists.
 *
 * A LIST RATHER THAN A SELECT. The CRM has more tasks than a dropdown can hold,
 * and the operator starting a clock almost always wants one of a handful of
 * recent ones. So the list opens filled - the newest tasks that are not finished
 * - and typing narrows it through the same `q` the task register searches by.
 * Nobody has to know a task's name in full to find it.
 *
 * DONE TASKS ARE FILTERED HERE, not by the query, because the list endpoint
 * takes exactly one status and "anything except done" is not one of them. That
 * is why more rows are read than are drawn.
 *
 * THE NOTE IS OPTIONAL and is the same field the stop dialog shows: whatever is
 * typed here rides along to the time entry when the clock is stopped. It is
 * offered at the start because that is when an operator knows what they are
 * about to do; at the stop they are already thinking about the next thing.
 *
 * THE PICKED TASK IS PINNED TO THE TOP once the query stops matching it. It is
 * kept rather than cleared, because narrowing the list is how somebody looks for
 * a SECOND candidate before deciding - but a choice that is still in force and
 * no longer on screen is the worst of both: the button says start and the
 * operator cannot see what it would start. So the row stays, above the results,
 * with its tick.
 *
 * WHY THIS DOES NOT CLOSE ITSELF ON EVERY CONFIRM: starting can run into another
 * clock, and the provider answers that by putting a second question up in this
 * dialog's place. Closing on a refusal would take that question with it. See
 * `startOn`.
 */
import { useEffect, useState } from "react";
import { Check, Loader2, Play, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { getTasks, type Task } from "../../lib/projects";
import { Btn, Modal } from "../ui";
import { useTimers } from "./TimerProvider";

/** How many suggestions are drawn. Enough to recognise one, few enough that the
 *  list does not become a second task register inside a dialog. */
const SHOW = 8;
/** How many are read to fill those rows - see the head on why the two differ. */
const FETCH = 40;

export function TimerStartModal() {
  const t = useT();
  const timers = useTimers();
  const [q, setQ] = useState("");
  /** Null until the FIRST answer arrives, so "nothing matches" and "still
   *  asking" can be drawn as the different things they are. Every later query
   *  leaves the previous rows standing: a list that blanked on each keystroke
   *  would flash empty under the finger that is still typing. */
  const [rows, setRows] = useState<Task[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<Task | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const query = q.trim();
    let alive = true;
    setLoading(true);
    // The first list is not debounced: it is the dialog opening, and a quarter
    // of a second of empty box before anything appears reads as a broken form.
    const id = window.setTimeout(
      () => {
        getTasks({ q: query || undefined, pageSize: FETCH })
          .then((page) => {
            if (!alive) return;
            setRows(page.rows.filter((r) => r.status !== "done").slice(0, SHOW));
            setLoading(false);
          })
          .catch(() => {
            if (!alive) return;
            setRows([]);
            setLoading(false);
          });
      },
      query ? 250 : 0,
    );
    return () => {
      alive = false;
      window.clearTimeout(id);
    };
  }, [q]);

  if (!timers) return null;

  async function start() {
    if (!timers || !picked) return;
    setBusy(true);
    const started = await timers.startOn(picked.id, note.trim() || undefined);
    setBusy(false);
    // Only on a real start. A refusal leaves the form standing so the operator
    // can try another task, and a question has already replaced this dialog.
    if (started) timers.closePending();
  }

  // The pick first, then whatever the query found, with the pick never drawn
  // twice. See the head.
  const shown = rows === null ? null
    : picked && !rows.some((r) => r.id === picked.id) ? [picked, ...rows]
    : rows;

  const title = t("timer.startTitle");

  return (
    <Modal
      title={title}
      label={title}
      onClose={timers.closePending}
      className="max-w-[460px]"
      footer={
        <>
          <Btn onClick={timers.closePending} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn tone="dark" onClick={() => void start()} disabled={busy || !picked}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
            {t("timer.start")}
          </Btn>
        </>
      }
    >
      <div className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("timer.pickTask")} *</span>
          <label className="crm-inp">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("timer.searchTasks")}
              aria-label={t("timer.searchTasks")}
              autoComplete="off"
              autoFocus
            />
            {loading ? (
              <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Search className="size-4 shrink-0 text-muted-foreground" />
            )}
          </label>
        </div>

        <ul className="max-h-[260px] overflow-auto rounded-2xl bg-[var(--crm-sunk)] p-1.5">
          {shown === null ? (
            <li className="crm-pal-empty">{t("common.loading")}</li>
          ) : shown.length === 0 ? (
            <li className="crm-pal-empty">{t("timer.noTasks")}</li>
          ) : (
            shown.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  className={cn("crm-mi", picked?.id === task.id && "is-on")}
                  aria-pressed={picked?.id === task.id}
                  onClick={() => setPicked(task)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="crm-trunc block">{task.title}</span>
                    {task.project ? (
                      <span className="crm-mi-sub crm-trunc block">{task.project.name}</span>
                    ) : null}
                  </span>
                  {/* The tick is the choice; `is-on` alone is a background one
                      shade off the panel behind it, which on the dark theme is
                      a difference nobody can see. Held at zero opacity on the
                      other rows so picking one does not shift the text of the
                      rest sideways. */}
                  <Check className={cn("size-4", picked?.id !== task.id && "opacity-0")} />
                </button>
              </li>
            ))
          )}
        </ul>

        <div>
          <span className="crm-lab">{t("projects.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("projects.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>
      </div>
    </Modal>
  );
}
